import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PaymentVerificationDto } from "@/services/admin-payment.service";
import { formatDistanceToNow } from "date-fns";
import { formatCurrency } from "@/lib/currency";
import { Loader2, Paperclip, Clock, Building2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface PaymentVerificationQueueProps {
  payments: PaymentVerificationDto[];
  selectedPaymentId: string | null;
  onSelectPayment: (paymentId: string) => void;
  totalCount?: number;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  /** The last "Load More" request failed; the button turns into an inline retry. */
  loadMoreFailed?: boolean;
}

export function PaymentVerificationQueue({
  payments,
  selectedPaymentId,
  onSelectPayment,
  totalCount,
  onLoadMore,
  isLoadingMore,
  loadMoreFailed,
}: PaymentVerificationQueueProps) {
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="mb-3 flex items-center justify-between shrink-0">
        <h2 className="text-base font-semibold tracking-tight text-foreground flex items-center gap-2">
          Verification Queue
          {payments.length > 0 && (
            <Badge variant="secondary" className="rounded-full text-xs font-medium px-2 py-0.5">
              {payments.length}
            </Badge>
          )}
        </h2>
      </div>
      <ScrollArea className="flex-1 min-h-0 pr-1">
        <div className="space-y-2.5">
          {payments.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm border rounded-xl bg-card/50">
              No payments awaiting verification
            </div>
          ) : (
            payments.map((payment) => {
              const isSelected = selectedPaymentId === payment.paymentId;
              const hasProof =
                payment.attachments.length > 0 || payment.paymentProofUrl;
              const proofCount =
                payment.attachments.length > 0
                  ? payment.attachments.length
                  : 1;

              return (
                <div
                  key={payment.paymentId}
                  className={cn(
                    "group relative rounded-xl border p-3.5 cursor-pointer transition-all duration-200 shadow-2xs hover:shadow-sm",
                    isSelected
                      ? "border-primary/40 bg-primary/[0.04] ring-2 ring-primary/20 border-l-4 border-l-primary"
                      : "border-border/80 bg-card hover:bg-muted/40 hover:border-border",
                  )}
                  onClick={() => onSelectPayment(payment.paymentId)}
                >
                  {/* Top row: Order number & Amount */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm truncate text-primary group-hover:underline">
                        {payment.paymentOrderNumber}
                      </p>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                        <Building2 className="h-3 w-3 shrink-0" />
                        <span className="truncate">{payment.tenantName}</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-bold text-sm text-foreground">
                        {formatCurrency(payment.amount, payment.currency)}
                      </p>
                    </div>
                  </div>

                  {/* Bottom row: Method badge, Time ago, Proof badge */}
                  <div className="flex items-center justify-between gap-2 pt-2 border-t border-border/40 text-xs">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[11px] font-normal px-2 py-0.5 rounded-md">
                        {payment.paymentMethod}
                      </Badge>
                      <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {formatDistanceToNow(new Date(payment.createdAt), {
                          addSuffix: true,
                        })}
                      </span>
                    </div>

                    {hasProof && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                        <Paperclip className="h-3 w-3" />
                        {proofCount} proof{proofCount > 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
        {onLoadMore &&
          totalCount !== undefined &&
          payments.length < totalCount && (
            <div className="pt-2 space-y-1.5">
              {loadMoreFailed && (
                <p className="text-center text-xs text-destructive-ink">
                  Could not load more payments.
                </p>
              )}
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={onLoadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? (
                  <>
                    <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                    Loading...
                  </>
                ) : loadMoreFailed ? (
                  "Retry"
                ) : (
                  `Load More (${totalCount - payments.length} remaining)`
                )}
              </Button>
            </div>
          )}
      </ScrollArea>
    </div>
  );
}
